import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

const SEVERITIES = ['info', 'warning', 'high', 'critical']

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-text-subtle',
  warning: 'bg-warning',
  high: 'bg-warning',
  critical: 'bg-danger',
}

export function Security() {
  const { t, i18n } = useTranslation('admin')
  const [severity, setSeverity] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-security', severity, page],
    queryFn: () => adminApi.listSecurity({ ...(severity ? { severity } : {}), page }),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('security.title')}</h1>
      <p className="text-text-muted">{t('security.subtitle')}</p>

      <div className="flex flex-wrap items-end gap-3">
        <select
          className="clari-input max-w-xs"
          value={severity}
          onChange={(e) => {
            setSeverity(e.target.value)
            setPage(1)
          }}
          aria-label={t('security.severity_filter')}
        >
          <option value="">{t('common.all')}</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {t(`severities.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('security.col_time')}</th>
                <th className="px-4 py-2 font-medium">{t('security.col_severity')}</th>
                <th className="px-4 py-2 font-medium">{t('security.col_event')}</th>
                <th className="px-4 py-2 font-medium">{t('security.col_ip')}</th>
                <th className="px-4 py-2 font-medium">{t('security.col_user')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-text-muted">{formatDate(e.timestamp, i18n.language)}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={['h-2 w-2 rounded-full', SEVERITY_DOT[e.severity] ?? 'bg-text-subtle'].join(' ')}
                        aria-hidden="true"
                      />
                      {t(`severities.${e.severity}`, { defaultValue: e.severity })}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono">{e.event_type}</td>
                  <td className="px-4 py-2 font-mono">{e.ip_address ?? '—'}</td>
                  <td className="px-4 py-2 font-mono">{e.user_id ?? '—'}</td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                    {t('security.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
